/**
 * Language switch. `t(key)` reads the current language signal, so any
 * component that calls it re-renders when the language changes.
 */
import { signal } from '@preact/signals';
import { bn, en, type StringKey } from './strings.js';

export type Lang = 'en' | 'bn';

const STORAGE_KEY = 'khata.lang';

function initial(): Lang {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'bn' || saved === 'en') return saved;
  } catch {
    // no storage
  }
  return 'en';
}

export const lang = signal<Lang>(initial());

export function setLang(next: Lang): void {
  lang.value = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // no storage
  }
  if (typeof document !== 'undefined') document.documentElement.lang = next;
}

if (typeof document !== 'undefined') document.documentElement.lang = lang.peek();

const tables: Record<Lang, Record<StringKey, string>> = { en, bn };

/** Fill `{name}` placeholders. Values are inserted as-is (already formatted). */
export function fill(template: string, params?: Readonly<Record<string, string | number>>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in params ? String(params[k]) : m));
}

export function t(key: StringKey, params?: Readonly<Record<string, string | number>>): string {
  return fill(tables[lang.value][key], params);
}

/** Same as `t` but for a language given explicitly (tests, non-reactive code). */
export function tIn(
  l: Lang,
  key: StringKey,
  params?: Readonly<Record<string, string | number>>,
): string {
  return fill(tables[l][key], params);
}

const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];

/** '৳1,234.50' → '৳১,২৩৪.৫০' */
export function toBengaliDigits(s: string): string {
  return s.replace(/[0-9]/g, (d) => BN_DIGITS[Number(d)]!);
}

/** '১,২৩৪.৫' → '1,234.5' so typed Bangla numbers parse. */
export function toLatinDigits(s: string): string {
  return s.replace(/[০-৯]/g, (d) => String(BN_DIGITS.indexOf(d)));
}

/** Digits in the current language. */
export function digits(s: string): string {
  return lang.value === 'bn' ? toBengaliDigits(s) : s;
}

/** Count with a unit word: plural('3', one, many). */
export function count(n: number, one: StringKey, many: StringKey): string {
  return `${digits(String(n))} ${t(n === 1 ? one : many)}`;
}

/** Re-phrase a core/server error in the current language when we know how; otherwise keep its message. */
export function translateError(
  error: {
    code: string;
    message: string;
    params?: Readonly<Record<string, string | number>>;
    field?: string;
  },
  context?: 'sale' | 'purchase',
): string {
  if (lang.value === 'en') return error.message;
  const p = error.params ?? {};
  switch (error.code) {
    case 'must_be_fully_paid':
      return t(
        context === 'purchase' ? 'err_must_be_fully_paid_purchase' : 'err_must_be_fully_paid',
      );
    case 'paid_exceeds_total':
      return t('err_paid_exceeds_total');
    case 'insufficient_stock': {
      const where = t(p['bin'] === 'defective' ? 'err_where_defective' : 'err_where_stock');
      if (p['available'] === 0) return t('err_insufficient_stock_none', { ...p, where });
      return t('err_insufficient_stock', {
        ...p,
        where,
        availableText: toBengaliDigits(String(p['availableText'] ?? '')),
      });
    }
    case 'conflict':
      return t('err_conflict');
    case 'void_blocked':
      return t('err_void_blocked', { item: String(p['item'] ?? '') });
    case 'already_voided':
      return t('err_already_voided');
    case 'wrong_pin':
      return t('err_wrong_pin');
    case 'wrong_login':
      return t('err_wrong_login');
    case 'forbidden':
      return t('err_forbidden');
    case 'locked':
      return t('err_locked');
    case 'same_account':
      return t('err_same_account');
    case 'duplicate_item':
      return t('err_duplicate_item', { item: String(p['item'] ?? '') });
    default:
      return error.message;
  }
}
